import React, { useState, useMemo } from 'react';
import { Card, Row, Col, Button, Form, Alert } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { fetchWithAuth } from '@app/utils/httpClient';
import { 
    ASSET_API, 
    DEPARTMENT_API, 
    QUALITYLEVEL_API,
    CONNECTIONTYPE_API,
    USAGEPURPOSE_API,
    EQUIPMENTTYPE_API,
    EQUIPMENTSOURCE_API,
    FUNDINGSOURCE_API,
    ASSETGROUP_API,
    ASSETSTATUS_API
} from '@app/utils/apiUrl';
import { Get } from '@app/utils/httpProvider';
import ReactSpinner from '@app/components/reactSpinner/ReactSpinner';

// Utility for select styles
const selectStyles = {
    control: (base) => ({
        ...base,
        minHeight: '38px',
        borderColor: '#ced4da',
        boxShadow: 'none',
        '&:hover': {
            borderColor: '#80bdff'
        }
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 })
};

// Normalize data util
const normalizeData = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (res.data && Array.isArray(res.data)) return res.data;
    if (res.Data && Array.isArray(res.Data)) return res.Data;
    return [];
};

const ALL_FIELDS = [
    { key: 'assetCode', label: 'Mã tài sản', category: 'Cơ bản', defaultChecked: true },
    { key: 'assetName', label: 'Tên tài sản', category: 'Cơ bản', defaultChecked: true },
    { key: 'assetGroupName', label: 'Nhóm tài sản', category: 'Cơ bản', defaultChecked: true },
    { key: 'departmentName', label: 'Khoa/Phòng', category: 'Cơ bản', defaultChecked: true },
    { key: 'statusName', label: 'Trạng thái', category: 'Cơ bản', defaultChecked: true },
    { key: 'serialNumber', label: 'Số Serial', category: 'Thông số kỹ thuật', defaultChecked: false },
    { key: 'model', label: 'Model', category: 'Thông số kỹ thuật', defaultChecked: false },
    { key: 'manufacturer', label: 'Nhà sản xuất', category: 'Thông số kỹ thuật', defaultChecked: false },
    { key: 'country', label: 'Xuất xứ', category: 'Thông số kỹ thuật', defaultChecked: false },
    { key: 'powerSpec', label: 'Thông số KT', category: 'Thông số kỹ thuật', defaultChecked: false },
    { key: 'purchaseDate', label: 'Ngày mua', category: 'Tài chính', defaultChecked: false },
    { key: 'purchaseValue', label: 'Giá trị mua', category: 'Tài chính', defaultChecked: false },
    { key: 'usagePurposeName', label: 'Mục đích sử dụng', category: 'Phân loại', defaultChecked: false },
    { key: 'equipmentTypeName', label: 'Loại thiết bị', category: 'Phân loại', defaultChecked: false },
    { key: 'equipmentSourceName', label: 'Nguồn gốc', category: 'Phân loại', defaultChecked: false },
    { key: 'fundingSourceName', label: 'Nguồn kinh phí', category: 'Phân loại', defaultChecked: false },
    { key: 'qualityLevelName', label: 'Mức chất lượng', category: 'Phân loại', defaultChecked: false },
    { key: 'connectionTypeName', label: 'Kiểu kết nối', category: 'Phân loại', defaultChecked: false },
    { key: 'unitOfMeasurement', label: 'Đơn vị tính', category: 'Khác', defaultChecked: false },
    { key: 'manager', label: 'Người quản lý', category: 'Khác', defaultChecked: false },
    { key: 'qrCode', label: 'Mã QR', category: 'Khác', defaultChecked: false },
    { key: 'notes', label: 'Ghi chú', category: 'Khác', defaultChecked: false },
    { key: 'isDisposed', label: 'Đã thanh lý', category: 'Khác', defaultChecked: false },
];

const AssetReport = () => {
    const history = useHistory();

    const [isExporting, setIsExporting] = useState(false);
    
    // Filter states
    const [keyword, setKeyword] = useState('');
    const [departmentId, setDepartmentId] = useState(null);
    const [assetGroupId, setAssetGroupId] = useState(null);
    const [assetStatusId, setAssetStatusId] = useState(null);
    const [usagePurposeId, setUsagePurposeId] = useState(null);
    const [equipmentTypeId, setEquipmentTypeId] = useState(null);
    const [equipmentSourceId, setEquipmentSourceId] = useState(null);
    const [fundingSourceId, setFundingSourceId] = useState(null);
    const [qualityLevelId, setQualityLevelId] = useState(null);
    const [connectionTypeId, setConnectionTypeId] = useState(null);
    const [isDisposedFilter, setIsDisposedFilter] = useState(false);



    // Helper function for dropdown options
    const getOptions = (raw, labelField, valueField = 'Id', allLabel = '-- Tất cả --') => {
        const list = normalizeData(raw);
        return [{ value: null, label: allLabel }, ...list.map(d => ({ 
            value: d[valueField] || d.id, 
            label: d[labelField] || d[labelField.charAt(0).toLowerCase() + labelField.slice(1)] 
        }))];
    };

    // Queries for filters
    const { data: deptsRaw } = useQuery(['getAllDeptsReport_v2'], () => Get(DEPARTMENT_API.GET_ALL));
    const { data: groupsRaw } = useQuery(['getAllGroupsReport_v2'], () => Get(ASSETGROUP_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));
    const { data: statusesRaw } = useQuery(['getAllStatusesReport_v2'], () => Get(ASSETSTATUS_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));
    const { data: qualityLevelsRaw } = useQuery(['getAllQualityLevelsReport_v2'], () => Get(QUALITYLEVEL_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));
    const { data: connectionTypesRaw } = useQuery(['getAllConnectionTypesReport_v2'], () => Get(CONNECTIONTYPE_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));
    const { data: usagePurposesRaw } = useQuery(['getAllUsagePurposesReport_v2'], () => Get(USAGEPURPOSE_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));
    const { data: equipmentTypesRaw } = useQuery(['getAllEquipmentTypesReport_v2'], () => Get(EQUIPMENTTYPE_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));
    const { data: equipmentSourcesRaw } = useQuery(['getAllEquipmentSourcesReport_v2'], () => Get(EQUIPMENTSOURCE_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));
    const { data: fundingSourcesRaw } = useQuery(['getAllFundingSourcesReport_v2'], () => Get(FUNDINGSOURCE_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));

    const departmentOptions = useMemo(() => {
        const list = normalizeData(deptsRaw);
        return [{ value: null, label: '-- Tất cả Khoa/Phòng --' }, ...list.map(d => ({ value: d.id, label: d.departmentName }))];
    }, [deptsRaw]);

    const groupOptions = useMemo(() => getOptions(groupsRaw, 'AssetGroupName', 'Id', '-- Tất cả Nhóm --'), [groupsRaw]);
    const statusOptions = useMemo(() => getOptions(statusesRaw, 'StatusName', 'Id', '-- Tất cả Trạng thái --'), [statusesRaw]);
    const qualityLevelOptions = useMemo(() => getOptions(qualityLevelsRaw, 'QualityLevelName'), [qualityLevelsRaw]);
    const connectionTypeOptions = useMemo(() => getOptions(connectionTypesRaw, 'ConnectionTypeName'), [connectionTypesRaw]);
    const usagePurposeOptions = useMemo(() => getOptions(usagePurposesRaw, 'UsagePurposeName'), [usagePurposesRaw]);
    const equipmentTypeOptions = useMemo(() => getOptions(equipmentTypesRaw, 'EquipmentTypeName'), [equipmentTypesRaw]);
    const equipmentSourceOptions = useMemo(() => getOptions(equipmentSourcesRaw, 'EquipmentSourceName'), [equipmentSourcesRaw]);
    const fundingSourceOptions = useMemo(() => getOptions(fundingSourcesRaw, 'FundingSourceName'), [fundingSourcesRaw]);

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const requestBody = {
                Fields: ALL_FIELDS.map(f => f.key),
                Keyword: keyword,
                CurrentDepartmentId: departmentId,
                AssetGroupId: assetGroupId,
                AssetStatusId: assetStatusId,
                EquipmentTypeId: equipmentTypeId,
                UsagePurposeId: usagePurposeId,
                QualityLevelId: qualityLevelId,
                EquipmentSourceId: equipmentSourceId,
                FundingSourceId: fundingSourceId,
                ConnectionTypeId: connectionTypeId,
                IsDisposed: isDisposedFilter ? true : null
            };

            const response = await fetchWithAuth(ASSET_API.EXPORT_REPORT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            if (response.ok) {
                const blob = await response.blob();
                const fileName = `BaoCaoTaiSan_${new Date().getTime()}.xlsx`;
                
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success('Xuất báo cáo thành công!');
            } else {
                toast.error('Có lỗi xảy ra khi xuất báo cáo.');
            }
        } catch (error) {
            console.error('Lỗi export report:', error);
            toast.error('Lỗi kết nối máy chủ.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="container-fluid">
            {isExporting && <ReactSpinner />}
            
            <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white py-3">
                    <h5 className="mb-0 fw-bold text-primary">
                        <i className="fas fa-file-export mr-2" /> Báo cáo / Xuất Excel Tài sản
                    </h5>
                </Card.Header>
                <Card.Body className="p-4">
                    
                    <Alert variant="info" className="mb-4">
                        <i className="fas fa-info-circle mr-2" />
                        Lựa chọn các tiêu chí lọc để giới hạn dữ liệu xuất. Sau đó chọn các cột bạn muốn hiển thị trong file Excel.
                    </Alert>

                    <h6 className="fw-bold mb-3 border-bottom pb-2">1. Điều kiện lọc (Tuỳ chọn)</h6>
                    <Row className="mb-4">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Từ khóa tìm kiếm</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    placeholder="Mã, tên tài sản..." 
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Khoa/Phòng</Form.Label>
                                <Select
                                    styles={selectStyles}
                                    options={departmentOptions}
                                    value={departmentOptions.find(o => o.value === departmentId) || departmentOptions[0]}
                                    onChange={opt => setDepartmentId(opt?.value ?? null)}
                                    menuPortalTarget={document.body}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Nhóm tài sản</Form.Label>
                                <Select
                                    styles={selectStyles}
                                    options={groupOptions}
                                    value={groupOptions.find(o => o.value === assetGroupId) || groupOptions[0]}
                                    onChange={opt => setAssetGroupId(opt?.value ?? null)}
                                    menuPortalTarget={document.body}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Trạng thái</Form.Label>
                                <Select
                                    styles={selectStyles}
                                    options={statusOptions}
                                    value={statusOptions.find(o => o.value === assetStatusId) || statusOptions[0]}
                                    onChange={opt => setAssetStatusId(opt?.value ?? null)}
                                    menuPortalTarget={document.body}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="font-weight-bold text-muted small mb-1">Loại thiết bị</Form.Label>
                                <Select
                                    styles={selectStyles}
                                    options={equipmentTypeOptions}
                                    value={equipmentTypeOptions.find(o => o.value === equipmentTypeId) || equipmentTypeOptions[0]}
                                    onChange={opt => setEquipmentTypeId(opt?.value ?? null)}
                                    menuPortalTarget={document.body}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="font-weight-bold text-muted small mb-1">Mục đích sử dụng</Form.Label>
                                <Select
                                    styles={selectStyles}
                                    options={usagePurposeOptions}
                                    value={usagePurposeOptions.find(o => o.value === usagePurposeId) || usagePurposeOptions[0]}
                                    onChange={opt => setUsagePurposeId(opt?.value ?? null)}
                                    menuPortalTarget={document.body}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="font-weight-bold text-muted small mb-1">Nguồn gốc</Form.Label>
                                <Select
                                    styles={selectStyles}
                                    options={equipmentSourceOptions}
                                    value={equipmentSourceOptions.find(o => o.value === equipmentSourceId) || equipmentSourceOptions[0]}
                                    onChange={opt => setEquipmentSourceId(opt?.value ?? null)}
                                    menuPortalTarget={document.body}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="font-weight-bold text-muted small mb-1">Nguồn kinh phí</Form.Label>
                                <Select
                                    styles={selectStyles}
                                    options={fundingSourceOptions}
                                    value={fundingSourceOptions.find(o => o.value === fundingSourceId) || fundingSourceOptions[0]}
                                    onChange={opt => setFundingSourceId(opt?.value ?? null)}
                                    menuPortalTarget={document.body}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="font-weight-bold text-muted small mb-1">Mức chất lượng</Form.Label>
                                <Select
                                    styles={selectStyles}
                                    options={qualityLevelOptions}
                                    value={qualityLevelOptions.find(o => o.value === qualityLevelId) || qualityLevelOptions[0]}
                                    onChange={opt => setQualityLevelId(opt?.value ?? null)}
                                    menuPortalTarget={document.body}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="font-weight-bold text-muted small mb-1">Kiểu kết nối</Form.Label>
                                <Select
                                    styles={selectStyles}
                                    options={connectionTypeOptions}
                                    value={connectionTypeOptions.find(o => o.value === connectionTypeId) || connectionTypeOptions[0]}
                                    onChange={opt => setConnectionTypeId(opt?.value ?? null)}
                                    menuPortalTarget={document.body}
                                />
                            </Form.Group>
                        </Col>
                        {/* <Col md={6} className="d-flex align-items-center">
                            <div className="custom-control custom-checkbox mt-3">
                                <input 
                                    type="checkbox" 
                                    className="custom-control-input" 
                                    id="isDisposedFilter"
                                    checked={isDisposedFilter}
                                    onChange={(e) => setIsDisposedFilter(e.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor="isDisposedFilter">
                                    Đánh dấu bao gồm tài sản đã được thanh lý
                                </label>
                            </div>
                        </Col> */}
                    </Row>


                    <div className="text-center mt-5">
                        <Button
                            variant="success"
                            size="lg"
                            className="px-5 shadow-sm"
                            disabled={isExporting}
                            onClick={handleExport}
                        >
                            <i className="fas fa-file-excel mr-2" />
                            {isExporting ? 'Đang tạo báo cáo...' : 'Xuất Báo Cáo Excel'}
                        </Button>
                    </div>

                </Card.Body>
            </Card>
        </div>
    );
};

export default AssetReport;
